import React from "react";
import { styled } from "@mui/material/styles";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import Input from "../../../../component-lib/Atoms/Input/Input";
import Button from "../../../../component-lib/Molecules/button/Button";
import EventEHRSwitchButton from "../../../../component-lib/Molecules/switch-button/EventEHRSwitchButton";
import { TEXT_CONSTANTS } from "../../../../utils/text-constants/textConstants";

const SmallBootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: "0px 24px 16px 24px",
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
  "& .MuiPaper-root": {
    borderRadius: "8px",
    width: "600px",
    maxWidth: "none",
  },
}));

const ROLE_NAME_FIELD = "roleName";
const DESCRIPTION_FIELD = "description";
const STATUS_FIELD = "status";

interface AddNewRoleItemDialogProps {
  isOpen: boolean;
  handleClose: () => void;
  handleData: (data: {
    roleName: string;
    description: string;
    status: "Active" | "InActive";
  }) => void;
  isEdit?: boolean;
  defaultMasterRoleData?: any;
}

export default function AddNewRoleItemDialog({
  isOpen,
  handleClose,
  handleData,
  isEdit = false,
  defaultMasterRoleData,
}: AddNewRoleItemDialogProps) {
  const dialogHeading = isEdit ? "Edit Role" : "Add Role";

  const validationSchema = Yup.object().shape({
    [ROLE_NAME_FIELD]: Yup.string()
      .trim()
      .max(50, "Role name should not exceed 50 characters")
      .required("Role name is required"),
  });

  return (
    <SmallBootstrapDialog
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          handleClose();
        }
      }}
      aria-labelledby="add-new-role-item-title"
      open={isOpen}
    >
      <Formik
        initialValues={{
          [ROLE_NAME_FIELD]: defaultMasterRoleData?.name || "",
          [DESCRIPTION_FIELD]: defaultMasterRoleData?.description || "",
          [STATUS_FIELD]: (defaultMasterRoleData?.status === "InActive"
            ? "InActive"
            : "Active") as "Active" | "InActive",
        }}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={(values, { setSubmitting, resetForm }) => {
          try {
            handleData({
              roleName: values[ROLE_NAME_FIELD].trim(),
              description: (values[DESCRIPTION_FIELD] as string).trim(),
              status: values[STATUS_FIELD] as "Active" | "InActive",
            });
            resetForm();
          } catch (error) {
            console.log(error);
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({
          errors,
          values,
          touched,
          setFieldValue,
          handleBlur,
          handleSubmit,
        }) => {
          const isActive = values[STATUS_FIELD] === "Active";
          return (
            <Form onSubmit={handleSubmit}>
              <DialogTitle
                sx={{
                  m: 0,
                  p: 2,
                  fontWeight: 600,
                  fontSize: "18px",
                  fontFamily: "inter",
                }}
                id="add-new-role-item-title"
              >
                {dialogHeading}
              </DialogTitle>
              <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={(theme) => ({
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: theme.palette.grey[500],
                })}
              >
                <CloseIcon />
              </IconButton>
              <hr />
              <DialogContent>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr",
                    gap: "16px",
                  }}
                >
                  <div className="pt-4">
                    <Input
                      textFieldLabel="Role Name"
                      size="small"
                      name={ROLE_NAME_FIELD}
                      value={values[ROLE_NAME_FIELD]}
                      maxLength={50}
                      onChangeCb={(e) => {
                        setFieldValue(ROLE_NAME_FIELD, e.target.value);
                      }}
                      onBlur={handleBlur}
                      error={
                        touched[ROLE_NAME_FIELD] &&
                        Boolean(errors[ROLE_NAME_FIELD])
                      }
                      helperText={
                        touched[ROLE_NAME_FIELD]
                          ? (errors[ROLE_NAME_FIELD] as string | undefined)
                          : undefined
                      }
                    />
                  </div>
                  <div>
                    <Input
                      textFieldLabel="Description"
                      multiline={true}
                      maxWords={250}
                      rows={4}
                      size="small"
                      name={DESCRIPTION_FIELD}
                      value={values[DESCRIPTION_FIELD] as string}
                      onChangeCb={(e) => {
                        setFieldValue(DESCRIPTION_FIELD, e.target.value);
                      }}
                      onBlur={handleBlur}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#393939",
                        fontWeight: 500,
                        marginRight: "8px",
                      }}
                    >
                      Status:
                    </span>
                    <EventEHRSwitchButton
                      checked={isActive}
                      onChange={(_e, checked) => {
                        setFieldValue(
                          STATUS_FIELD,
                          checked ? "Active" : "InActive",
                        );
                      }}
                      label={isActive ? "Active" : "InActive"}
                    />
                  </div>
                </div>
              </DialogContent>
              <DialogActions style={{ padding: "24px" }}>
                <Button
                  label={TEXT_CONSTANTS?.CANCEL}
                  variant="outlined"
                  onClickCb={handleClose}
                  style={{
                    width: "120px",
                    height: "44px",
                    padding: "12px 20px",
                    fontSize: "16px",
                    backgroundColor: "white",
                    border: "none",
                    color: "#393939",
                  }}
                />
                <Button
                  type="submit"
                  label={TEXT_CONSTANTS?.SAVE}
                  variant="contained"
                  onClickCb={() => {}}
                  style={{
                    width: "200px",
                    height: "44px",
                    padding: "12px 20px",
                    fontSize: "16px",
                  }}
                />
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </SmallBootstrapDialog>
  );
}
